import { useMemo } from "react";
import {
  analyze,
  COST_PROFILE_DEFAULTS,
  TRUCK_DEFAULTS,
  type AnalyzeInput,
  type CostProfile,
  type Load,
  type PayBreakdown,
  type PayContract,
  type Truck,
} from "@haulsheet/core";

export function buildAnalyzeInput(
  load: Pick<
    Load,
    | "linehaulCents"
    | "fscCentsPerMile"
    | "accessorialsCents"
    | "loadedMiles"
    | "deadheadMiles"
    | "routeMinutes"
    | "routeDeadheadMinutes"
  >,
  contract: PayContract | null | undefined,
  cost: CostProfile | null | undefined,
  truck: Truck | null | undefined,
): AnalyzeInput {
  return {
    linehaulCents: load.linehaulCents,
    fscCentsPerMile: load.fscCentsPerMile,
    accessorialsCents: load.accessorialsCents,
    loadedMiles: load.loadedMiles,
    deadheadMiles: load.deadheadMiles,
    cutBps: contract?.cutBps ?? 0,
    kind: contract?.kind,
    perLoadedMileCents: contract?.perLoadedMileCents,
    flatCents: contract?.flatCents,
    fscRule: contract?.fscRule ?? { kind: "from_load" },
    costRule: contract?.costRule ?? { kind: "per_mile" },
    mpg: truck?.mpg ?? TRUCK_DEFAULTS.mpg,
    dieselCentsPerGallon: cost?.dieselCentsPerGallon ?? 400,
    otherCentsPerMile: cost?.otherCentsPerMile ?? 30,
    targetCentsPerMile:
      cost?.targetCentsPerMile ?? COST_PROFILE_DEFAULTS.targetCentsPerMile,
    floorCentsPerMile:
      cost?.floorCentsPerMile ?? COST_PROFILE_DEFAULTS.floorCentsPerMile,
    routeMinutes: load.routeMinutes,
    routeDeadheadMinutes: load.routeDeadheadMinutes,
  };
}

export function useAnalyzeLoad(
  load: Parameters<typeof buildAnalyzeInput>[0] | null,
  contract: PayContract | null | undefined,
  cost: CostProfile | null | undefined,
  truck: Truck | null | undefined,
): PayBreakdown | null {
  return useMemo(() => {
    if (!load) return null;
    return analyze(buildAnalyzeInput(load, contract, cost, truck));
  }, [load, contract, cost, truck]);
}
